import { baseApi } from './baseApi';
import {
  InventoryCategory,
  CreateInventoryCategoryRequest,
  UpdateInventoryCategoryRequest,
  InventoryItem,
  CreateInventoryItemRequest,
  UpdateInventoryItemRequest,
  AdjustStockRequest,
  LowStockItem,
  InventoryMovement,
  RegisterWasteRequest,
  WasteReport,
  InventoryCostSummary,
  ProductMarginReport,
  ConsumptionReport,
  InventoryValuation,
} from '../types/inventory';

export const inventoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Categories
    getInventoryCategories: builder.query<InventoryCategory[], void>({
      query: () => '/inventory/categories',
      providesTags: ['InventoryCategory'],
    }),
    getInventoryCategory: builder.query<InventoryCategory, string>({
      query: (id) => `/inventory/categories/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'InventoryCategory', id }],
    }),
    createInventoryCategory: builder.mutation<InventoryCategory, CreateInventoryCategoryRequest>({
      query: (data) => ({ url: '/inventory/categories', method: 'POST', body: data }),
      invalidatesTags: ['InventoryCategory'],
    }),
    updateInventoryCategory: builder.mutation<InventoryCategory, { id: string; data: UpdateInventoryCategoryRequest }>({
      query: ({ id, data }) => ({ url: `/inventory/categories/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['InventoryCategory'],
    }),
    deleteInventoryCategory: builder.mutation<void, string>({
      query: (id) => ({ url: `/inventory/categories/${id}`, method: 'DELETE' }),
      invalidatesTags: ['InventoryCategory'],
    }),

    // Items
    getInventoryItems: builder.query<InventoryItem[], void>({
      query: () => '/inventory/items',
      providesTags: ['InventoryItem'],
    }),
    getInventoryItem: builder.query<InventoryItem, string>({
      query: (id) => `/inventory/items/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'InventoryItem', id }],
    }),
    createInventoryItem: builder.mutation<InventoryItem, CreateInventoryItemRequest>({
      query: (data) => ({ url: '/inventory/items', method: 'POST', body: data }),
      invalidatesTags: ['InventoryItem', 'InventoryCategory'],
    }),
    updateInventoryItem: builder.mutation<InventoryItem, { id: string; data: UpdateInventoryItemRequest }>({
      query: ({ id, data }) => ({ url: `/inventory/items/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['InventoryItem', 'InventoryCategory'],
    }),
    deleteInventoryItem: builder.mutation<void, string>({
      query: (id) => ({ url: `/inventory/items/${id}`, method: 'DELETE' }),
      invalidatesTags: ['InventoryItem', 'InventoryCategory'],
    }),

    // Import
    importInventoryItems: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/inventory/items/import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['InventoryItem', 'InventoryCategory', 'InventoryMovement'],
    }),

    // Stock
    adjustStock: builder.mutation<InventoryItem, { id: string; data: AdjustStockRequest }>({
      query: ({ id, data }) => ({ url: `/inventory/items/${id}/adjust-stock`, method: 'POST', body: data }),
      invalidatesTags: ['InventoryItem', 'InventoryMovement'],
    }),
    getLowStockItems: builder.query<LowStockItem[], void>({
      query: () => '/inventory/low-stock',
      providesTags: ['InventoryItem'],
    }),

    // Waste (merma)
    registerWaste: builder.mutation<InventoryMovement[], RegisterWasteRequest>({
      query: (data) => ({ url: '/inventory/waste', method: 'POST', body: data }),
      invalidatesTags: ['InventoryItem', 'InventoryMovement'],
    }),

    // Movements
    getInventoryMovements: builder.query<InventoryMovement[], { itemId?: string; type?: string; from?: string; to?: string }>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.itemId) searchParams.append('itemId', params.itemId);
        if (params.type) searchParams.append('type', params.type);
        if (params.from) searchParams.append('from', params.from);
        if (params.to) searchParams.append('to', params.to);
        return `/inventory/movements?${searchParams.toString()}`;
      },
      providesTags: ['InventoryMovement'],
    }),

    // Cost reports — from/to son fechas locales yyyy-MM-dd (como el P&L)
    getWasteReport: builder.query<WasteReport, { from: string; to: string }>({
      query: ({ from, to }) => `/inventory/reports/waste?from=${from}&to=${to}`,
      providesTags: ['InventoryMovement'],
    }),
    getInventoryCostSummary: builder.query<InventoryCostSummary, { from: string; to: string }>({
      query: ({ from, to }) => `/inventory/reports/cost-summary?from=${from}&to=${to}`,
      providesTags: ['InventoryMovement'],
    }),
    getProductMargins: builder.query<ProductMarginReport, { from: string; to: string }>({
      query: ({ from, to }) => `/inventory/reports/product-margins?from=${from}&to=${to}`,
      providesTags: ['InventoryMovement', 'Recipe'],
    }),
    getConsumptionReport: builder.query<ConsumptionReport, { from: string; to: string }>({
      query: ({ from, to }) => `/inventory/reports/consumption?from=${from}&to=${to}`,
      providesTags: ['InventoryMovement'],
    }),
    getInventoryValuation: builder.query<InventoryValuation, void>({
      query: () => '/inventory/reports/valuation',
      providesTags: ['InventoryItem', 'InventoryMovement'],
    }),
  }),
});

export const {
  useGetInventoryCategoriesQuery,
  useGetInventoryCategoryQuery,
  useCreateInventoryCategoryMutation,
  useUpdateInventoryCategoryMutation,
  useDeleteInventoryCategoryMutation,
  useGetInventoryItemsQuery,
  useGetInventoryItemQuery,
  useCreateInventoryItemMutation,
  useUpdateInventoryItemMutation,
  useDeleteInventoryItemMutation,
  useAdjustStockMutation,
  useRegisterWasteMutation,
  useGetLowStockItemsQuery,
  useGetInventoryMovementsQuery,
  useImportInventoryItemsMutation,
  useGetWasteReportQuery,
  useGetInventoryCostSummaryQuery,
  useGetProductMarginsQuery,
  useGetConsumptionReportQuery,
  useGetInventoryValuationQuery,
} = inventoryApi;
